// src/pages/CheckoutPage.js
import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import toast from "react-hot-toast";
import { apiRequest } from "../services/api";
import { getActiveCouponsCount } from "../services/coupons.api";
import {
  getActiveOrderDiscount,
  getActiveFreeShippingDiscount,
} from "../services/discounts.api";
import { getMe } from "../services/users.api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";

function CheckoutPage() {
  const { lang } = useContext(LanguageContext);
  const {
  cartItems,
  cartLoading,
  cartShareToken,
  subtotal,
  total,
  discount,
} = useCart();
  const navigate = useNavigate();
const location = useLocation();
const sharedBackGuardRef = useRef(false);

const safeNavigateBack = () => {
  const cameFromCartPage = location.state?.fromCartPage === true;

  if (cameFromCartPage) {
    sessionStorage.removeItem("velle-direct-shared-checkout");

    const params = new URLSearchParams();
    params.set("lang", lang);

    if (cartShareToken) {
      params.set("cart_token", cartShareToken);
    }

    navigate(`/cart?${params.toString()}`, { replace: true });
    return;
  }

  const openedFromSharedCheckoutLink =
    sessionStorage.getItem("velle-direct-shared-checkout") === "1";

  if (openedFromSharedCheckoutLink) {
    sessionStorage.removeItem("velle-direct-shared-checkout");
    navigate("/", { replace: true });
    return;
  }

  navigate("/", { replace: true });
};

  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [hasActiveCoupons, setHasActiveCoupons] = useState(true);
  const [shippingMethod, setShippingMethod] = useState("delivery");

  const [orderDiscount, setOrderDiscount] = useState(null);

  const [freeShippingDiscount, setFreeShippingDiscount] = useState(null);

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [governorate, setGovernorate] = useState("");
const [area, setArea] = useState("");
const [street, setStreet] = useState("");
const [building, setBuilding] = useState("");
  const [note, setNote] = useState("");
  const [noteFocused, setNoteFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [couponError, setCouponError] = useState(null);

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  // ---------------- LEBANON GOVERNORATES ----------------
const LEB_GOV = [
  { en: "Beirut", ar: "بيروت" },
  { en: "Mount Lebanon", ar: "جبل لبنان" },
  { en: "North Lebanon", ar: "لبنان الشمالي" },
  { en: "South Lebanon", ar: "لبنان الجنوبي" },
  { en: "Bekaa", ar: "البقاع" },
  { en: "Baalbek-Hermel", ar: "بعلبك الهرمل" },
  { en: "Nabatieh", ar: "النبطية" },
  { en: "Akkar", ar: "عكار" },
];

const normalizeGovernorate = (value = "") => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  const match = LEB_GOV.find(
    (gov) =>
      gov.en.toLowerCase() === raw.toLowerCase() ||
      gov.ar === raw
  );

  return match ? match.en : raw;
};

const validateFields = () => {
  let newErrors = {};

  if (!fullName.trim() || fullName.trim().length < 3)
    newErrors.fullName = "Invalid name";

  const cleanPhone = phone.replace(/\D/g, "");
  const lebaneseRegex = /^(961|0)?(3|70|71|76|78|79|81)\d{6}$/;
  if (!lebaneseRegex.test(cleanPhone))
    newErrors.phone = "Invalid Lebanese phone number";

  if (shippingMethod === "delivery") {
  if (!governorate) newErrors.governorate = "Select governorate";

  if (!area.trim() || area.trim().length < 2) {
    newErrors.area = "Enter area";
  }

  if (!street.trim() || street.trim().length < 2) {
    newErrors.street = "Enter street";
  }

  if (!building.trim() || building.trim().length < 1) {
    newErrors.building = "Enter building";
  }
}

  setErrors(newErrors);

  if (Object.keys(newErrors).length > 0) {
    toast.error("Please fill required fields");
    return false;
  }

  return true;
};

const formatPhone = (val) => {
  let digits = val.replace(/\D/g, "");

  if (digits.startsWith("961")) {
    const rest = digits.slice(3);
    return `+961 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 8)}`.trim();
  }

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`;
};

const handlePhoneChange = (e) => setPhone(formatPhone(e.target.value));

useEffect(() => {
  if (cartLoading) return;
  if (!cartShareToken) return;

  const params = new URLSearchParams(location.search);
  const existingToken = params.get("cart_token");

  if (existingToken === cartShareToken) return;

  params.set("cart_token", cartShareToken);

  const nextUrl = `${location.pathname}?${params.toString()}`;

  window.history.replaceState(null, "", nextUrl);
}, [cartLoading, cartShareToken, location.pathname, location.search]);

useEffect(() => {
  if (cartLoading) return;
  if (sharedBackGuardRef.current) return;
  if (location.pathname !== "/checkout") return;

  // ✅ Normal Cart → Checkout navigation must NOT be treated as a shared direct link
  if (location.state?.fromCartPage === true) return;

  const params = new URLSearchParams(window.location.search);
  const hasCartToken = Boolean(params.get("cart_token"));

  if (!hasCartToken) return;

  const currentState = window.history.state || {};
  const currentIdx =
    typeof currentState.idx === "number" ? currentState.idx : 0;

  // Normal navigation from cart to checkout should stay normal.
  if (currentIdx > 0) return;

  sharedBackGuardRef.current = true;
  sessionStorage.setItem("velle-direct-shared-checkout", "1");

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;

  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __velleSharedOrigin: "checkout",
    },
    "",
    currentUrl
  );

  window.history.pushState(
    {
      ...(window.history.state || {}),
      __velleSharedGuard: "checkout",
    },
    "",
    currentUrl
  );

  const handlePopState = (event) => {
    if (
      event.state?.__velleSharedOrigin === "checkout" ||
      sessionStorage.getItem("velle-direct-shared-checkout") === "1"
    ) {
      sessionStorage.removeItem("velle-direct-shared-checkout");
      navigate("/", { replace: true });
    }
  };

  window.addEventListener("popstate", handlePopState);

  return () => {
    window.removeEventListener("popstate", handlePopState);
  };
}, [cartLoading, cartShareToken, location.pathname, location.state, navigate]);

useEffect(() => {
  let mounted = true;

  const prefillCheckoutUser = async () => {
    const token = localStorage.getItem("esill-token");
    if (!token) return;

    try {
      let user = null;

      const cachedUser = localStorage.getItem("esill-user");

      if (cachedUser) {
        try {
          user = JSON.parse(cachedUser);
        } catch {
          user = null;
        }
      }

      // Always try to get the fresh profile from backend.
      const freshUser = await getMe().catch(() => null);

      if (freshUser) {
        user = freshUser;
        localStorage.setItem("esill-user", JSON.stringify(freshUser));
      }

      if (!mounted || !user) return;

      const rawPhone = user.phone || "";
      const rawEmail = user.email || "";
      const phoneLooksLikeEmail = rawPhone.includes("@");

      const normalizedPhone = phoneLooksLikeEmail ? "" : rawPhone;
      const normalizedEmail = rawEmail || (phoneLooksLikeEmail ? rawPhone : "");

      setFullName((prev) => prev || user.full_name || "");
      setPhone((prev) => prev || formatPhone(normalizedPhone || ""));
      setEmail((prev) => prev || normalizedEmail || "");

      setGovernorate((prev) =>
        prev ? prev : normalizeGovernorate(user.city || "")
      );

      setArea((prev) => prev || user.area || "");
setStreet((prev) => prev || user.street || "");
setBuilding((prev) => prev || user.building || "");
    } catch (err) {
      console.error("Failed to prefill checkout user", err);
    }
  };

  prefillCheckoutUser();

  return () => {
    mounted = false;
  };
}, []);

// ✅ SUBMIT ORDER (Esiell logic but using  localStorage keys)
const applyCoupon = async () => {
  if (!couponCode.trim()) return;

  setCouponLoading(true);
  try {
    const res = await apiRequest("/coupons/validate", {
      method: "POST",
      body: JSON.stringify({
        code: couponCode,
        subtotal: subtotal,
      }),
    });

    setCoupon(res);
    setCouponError(null);
    toast.success(lang === "EN" ? "Coupon applied" : "تم تطبيق الكوبون");
  } catch (err) {
    setCoupon(null);

    // ✅ Expected coupon errors (silent)
    if (err?.status === 400) {
      if (err.message === "unavailable") setCouponError("unavailable");
      else if (err.message === "expired") setCouponError("expired");
      else setCouponError("unavailable");
      return;
    }

    console.error(err);
    setCouponError("unavailable");
  } finally {
    setCouponLoading(false);
  }
};

useEffect(() => {
  const codeFromCart = location.state?.couponCode;

  if (!codeFromCart || coupon) return;

  setCouponCode(codeFromCart);

  const timer = setTimeout(() => {
    setCouponCode(codeFromCart);
  }, 0);

  return () => clearTimeout(timer);
}, [location.state?.couponCode, coupon]);

useEffect(() => {
  const codeFromCart = location.state?.couponCode;

  if (!codeFromCart || coupon || couponLoading) return;

  const applyFromCart = async () => {
    setCouponLoading(true);

    try {
      const res = await apiRequest("/coupons/validate", {
        method: "POST",
        body: JSON.stringify({
          code: codeFromCart,
          subtotal: subtotal,
        }),
      });

      setCoupon(res);
      setCouponError(null);
      setCouponCode(res.code || codeFromCart);
    } catch (err) {
      setCoupon(null);
      setCouponError("unavailable");
    } finally {
      setCouponLoading(false);
    }
  };

  applyFromCart();
}, [location.state?.couponCode, subtotal]);

// ✅ OPTIONAL: small helper (same idea as Esiell)
const getDeliveryMessage = () => {
  return lang === "EN"
    ? "Delivery within 2–3 days"
    : "سيتم التوصيل خلال يومين إلى ثلاثة أيام";
};

// ✅ SUBMIT ORDER (Esiell logic but using  localStorage keys)
const submitOrder = async () => {
  if (!cartItems.length) {
    toast.error(lang === "EN" ? "Your cart is empty" : "سلة التسوق فارغة");
    return;
  }

  if (!validateFields()) return;

  setLoading(true);

  try {
    let token = localStorage.getItem("esill-token");
const sessionToken = localStorage.getItem("esill-session");

    // 🔥 GUEST CHECKOUT FLOW
    if (!token) {
      const guest = await apiRequest("/auth/guest", {
        method: "POST",
        body: JSON.stringify({
          full_name: fullName,
          phone,
          email: email || null,
        }),
      });

      // 🔔 EXISTING ACCOUNT INFO
      if (guest.existed) {
        toast(
          lang === "EN"
            ? `You already have an account with this phone number:\n${guest.user.phone}`
            : `لديك حساب موجود مسبقاً بهذا الرقم:\n${guest.user.phone}`,
          {
            icon: "ℹ️",
            duration: 4000,
          }
        );
      }

      localStorage.setItem("esill-token", guest.token);
localStorage.setItem("esill-user", JSON.stringify(guest.user));
      token = guest.token;

      // 2️⃣ MERGE SESSION CART INTO USER CART
if (sessionToken) {
  await apiRequest("/cart/merge", {
    method: "POST",
    body: JSON.stringify({ session_token: sessionToken }),
  });

  // ✅ IMPORTANT:
  // Do NOT remove esill-session here.
  // Analytics must keep the same session from guest → user → order → thank-you.
  window.dispatchEvent(new Event("auth-changed"));
}
    }

    // 3️⃣ PLACE ORDER
    const payload = {
  full_name: fullName,
  phone,
  email: email || null,
  city: shippingMethod === "delivery" ? governorate : null,
  area: shippingMethod === "delivery" ? area : null,
  street: shippingMethod === "delivery" ? street : null,
  building: shippingMethod === "delivery" ? building : null,
  notes: note || null,
  payment_method: "cod",
  coupon_code: coupon?.code || null,
  country: "Lebanon",
  shipping_method: shippingMethod,
};

    const result = await apiRequest("/orders", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    toast.success(lang === "EN" ? "Order submitted successfully!" : "تم إرسال الطلب بنجاح!");

    const finalSubtotal = subtotal;
const finalDiscount = couponDiscountAmount;
const finalOrderDiscount = effectiveOrderDiscountAmount;
const finalTotal =
  finalSubtotal -
  finalOrderDiscount -
  finalDiscount +
  deliveryFee;

    // ✅ Save for thank-you page ( key)
    sessionStorage.setItem("esill-order",
      JSON.stringify({
        ...payload,
        items: cartItems.map((item) => ({
          public_id: item.public_id,
          qty: item.qty,
          price: item.price,
        })),
        subtotal: finalSubtotal,
        discount: finalDiscount, // coupon
order_discount: finalOrderDiscount,
        delivery_fee: deliveryFee,
        shipping_method: shippingMethod,
        total: finalTotal,
        itemCount: cartItems.reduce((a, b) => a + b.qty, 0),
        createdAt: new Date().toISOString(),
        order_id: result.order_id,
        order_number: result.order_number,
      })
    );

    navigate("/thank-you");
  } catch (err) {
    toast.error(err.message || (lang === "EN" ? "Order failed" : "فشل الطلب"));
    console.error(err);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  getActiveOrderDiscount()
    .then(setOrderDiscount)
    .catch(() => setOrderDiscount(null));
}, []);

useEffect(() => {
  getActiveFreeShippingDiscount()
    .then(setFreeShippingDiscount)
    .catch(() => setFreeShippingDiscount(null));
}, []);

  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: s.default_fee,
          free_enabled: s.free_enabled,
          free_threshold: s.free_threshold,
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    getActiveCouponsCount()
      .then((res) => setHasActiveCoupons(res.count > 0))
      .catch(() => setHasActiveCoupons(false));
  }, []);

  useEffect(() => {
  const token = localStorage.getItem("esill-token");

  // Only save guest abandoned checkout data here.
  // Logged-in abandoned carts already use user data.
  if (token) return;

  const hasContact =
    fullName.trim() ||
    phone.replace(/\D/g, "") ||
    email.trim();

  if (!hasContact) return;
  if (!cartItems.length) return;

  const timer = setTimeout(() => {
    apiRequest("/cart/checkout-contact", {
      method: "POST",
      body: JSON.stringify({
        full_name: fullName || null,
        phone: phone || null,
        email: email || null,
        city: shippingMethod === "delivery" ? governorate || null : null,
        area: shippingMethod === "delivery" ? area || null : null,
        street: shippingMethod === "delivery" ? street || null : null,
        building: shippingMethod === "delivery" ? building || null : null,
      }),
    }).catch(() => {});
  }, 700);

  return () => clearTimeout(timer);
}, [
  fullName,
  phone,
  email,
  governorate,
  area,
  street,
  building,
  shippingMethod,
  cartItems.length,
]);

 const getOrderDiscountAmount = () => {
  if (!orderDiscount) return 0;

  const subtotalValue = Number(subtotal) || 0;

  let valid = false;

  if (orderDiscount.min_requirement === "none") {
    valid = true;
  }

  if (
    orderDiscount.min_requirement === "amount" &&
    subtotalValue >= Number(orderDiscount.min_amount || 0)
  ) {
    valid = true;
  }

  if (
    orderDiscount.min_requirement === "qty" &&
    cartItems.reduce((a, b) => a + b.qty, 0) >= Number(orderDiscount.min_qty || 0)
  ) {
    valid = true;
  }

  if (!valid) return 0;

  // ✅ FIX STARTS HERE
  if (orderDiscount.discount_type === "percentage") {
    return Number(
      (subtotalValue * Number(orderDiscount.discount_value)) / 100
    );
  }

  if (orderDiscount.discount_type === "fixed") {
    return Number(orderDiscount.discount_value); // 🔥 FIX
  }

  return 0;
};

const orderDiscountAmount = getOrderDiscountAmount();
const safeOrderDiscountAmount = Number(orderDiscountAmount) || 0;

const couponDiscountAmount = Number(coupon?.discount || 0);

// ✅ IMPORTANT:
// Coupon codes should not stack with automatic order discounts.
// If a coupon exists, the automatic order discount becomes 0.
const effectiveOrderDiscountAmount = coupon
  ? 0
  : safeOrderDiscountAmount;

const totalItemsCount = cartItems.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const getFreeShippingDiscountInfo = () => {
  if (!freeShippingDiscount) {
    return {
      valid: false,
      label: "",
      endLabel: "",
    };
  }

  const subtotalValue = Number(subtotal || 0);
  const minRequirement = freeShippingDiscount.min_requirement || "none";
  const minAmount = Number(freeShippingDiscount.min_amount || 0);
  const minQty = Number(freeShippingDiscount.min_qty || 0);

  let valid = false;
  let label = "";

  if (minRequirement === "none") {
    valid = true;
    label =
      lang === "AR"
        ? "شحن مجاني على جميع الطلبات"
        : "Free delivery on all orders";
  }

  if (minRequirement === "amount") {
    valid = subtotalValue >= minAmount;

    const missingAmount = Math.max(0, minAmount - subtotalValue);

    label = valid
      ? lang === "AR"
        ? `تم تفعيل الشحن المجاني لأن طلبك وصل إلى $${minAmount}`
        : `Free delivery unlocked on orders over $${minAmount}`
      : lang === "AR"
      ? `أضيفي $${missingAmount.toFixed(2)} للحصول على شحن مجاني`
      : `Add $${missingAmount.toFixed(2)} more for free delivery`;
  }

  if (minRequirement === "qty") {
    valid = totalItemsCount >= minQty;

    const missingQty = Math.max(0, minQty - totalItemsCount);

    label = valid
      ? lang === "AR"
        ? `تم تفعيل الشحن المجاني عند شراء ${minQty}+ منتجات`
        : `Free delivery unlocked for ${minQty}+ items`
      : lang === "AR"
      ? `أضيفي ${missingQty} منتجات للحصول على شحن مجاني`
      : `Add ${missingQty} more item${missingQty === 1 ? "" : "s"} for free delivery`;
  }

  const endLabel = freeShippingDiscount.end_at
    ? new Date(freeShippingDiscount.end_at).toLocaleString(
        lang === "AR" ? "ar" : "en",
        {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }
      )
    : "";

  return {
    valid,
    label,
    endLabel,
  };
};

const freeShippingInfo = getFreeShippingDiscountInfo();

const isFreeDeliveryFromSettings =
  shipping.free_enabled &&
  Number(subtotal || 0) >= Number(shipping.free_threshold || 0);

const isFreeDeliveryFromDiscount = freeShippingInfo.valid;

const isFreeDelivery =
  isFreeDeliveryFromSettings || isFreeDeliveryFromDiscount;

const deliveryFee =
  shippingMethod === "pickup"
    ? 0
    : isFreeDelivery
    ? 0
    : Number(shipping.fee || 0);

    const checkoutTotal = Math.max(
  0,
  Number(subtotal || 0) -
    effectiveOrderDiscountAmount -
    couponDiscountAmount +
    Number(deliveryFee || 0)
);

  const TEXT = {
    EN: {
      title: "CHECKOUT",
      contact: "Contact Information",
      delivery: "Delivery",
      payment: "Payment",
      summary: "Order Summary",
      placeOrder: "PLACE ORDER",
    },
    AR: {
      title: "إتمام الشراء",
      contact: "معلومات التواصل",
      delivery: "التوصيل",
      payment: "الدفع",
      summary: "ملخص الطلب",
      placeOrder: "إتمام الطلب",
    },
  };

  const t = TEXT[lang];

  const sectionCard =
  "bg-[#F6EFE9] px-5 py-6 md:px-7 md:py-7 mb-6 border border-[#e6e6e6]";

const inputStyle =
  "w-full h-[48px] px-4 text-[15px] bg-white border border-[#cfcfcf] outline-none focus:border-black transition mb-3";

const textareaStyle =
  "w-full min-h-[92px] px-4 py-3 text-[15px] bg-white border border-[#cfcfcf] outline-none focus:border-black transition mb-3 resize-none";

const labelStyle =
  "block text-[13px] font-semibold uppercase tracking-[0.04em] text-black mb-2";

const errorStyle =
  "text-[12px] text-red-600 font-semibold -mt-1 mb-3";

const lineRow =
  "flex items-center justify-between gap-4 text-[14px] text-black";


const CheckoutSkeleton = () => (
  <div
    className="w-full min-h-screen bg-[#F6EFE9]"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 py-10 md:py-12 text-center">
        <div className="mx-auto h-[34px] w-[180px] bg-gray-200 animate-pulse rounded" />
        <div className="mx-auto mt-4 h-4 w-[260px] bg-gray-200 animate-pulse rounded" />
      </div>
    </section>

    <section className="bg-[#F6EFE9] pb-14 md:pb-16">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 pt-6 md:pt-8">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_390px] gap-6 md:gap-8 items-start">
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#F6EFE9] px-5 py-6 md:px-7 md:py-7 border border-[#e6e6e6]">
                <div className="h-6 w-[190px] bg-gray-200 animate-pulse rounded mb-6" />
                <div className="space-y-3">
                  <div className="h-[48px] w-full bg-gray-200 animate-pulse rounded" />
                  <div className="h-[48px] w-full bg-gray-200 animate-pulse rounded" />
                  <div className="h-[48px] w-full bg-gray-200 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>

          <aside className="md:sticky md:top-[160px] bg-[#F6EFE9] px-5 py-6 md:px-6 md:py-6 border border-[#e6e6e6]">
            <div className="h-6 w-[160px] bg-gray-200 animate-pulse rounded mb-6" />

            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="grid grid-cols-[54px_minmax(0,1fr)_auto] gap-3 items-start">
                  <div className="w-[54px] h-[72px] bg-gray-200 animate-pulse" />
                  <div>
                    <div className="h-4 w-[150px] bg-gray-200 animate-pulse rounded" />
                    <div className="mt-2 h-3 w-[70px] bg-gray-200 animate-pulse rounded" />
                  </div>
                  <div className="h-4 w-[45px] bg-gray-200 animate-pulse rounded" />
                </div>
              ))}
            </div>

            <div className="my-5 border-t border-black" />
            <div className="space-y-3">
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-[52px] w-full bg-gray-200 animate-pulse rounded mt-6" />
            </div>
          </aside>
        </div>
      </div>
    </section>
  </div>
);

const CheckoutEmptyState = () => (
  <div
    className="w-full min-h-screen bg-[#F6EFE9]"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
      <div className="max-w-[900px] mx-auto px-5 md:px-6 py-16 md:py-20 text-center">
        <h1 className="text-[30px] md:text-[36px] leading-none font-bold text-[#333]">
          {lang === "AR" ? "سلة التسوق فارغة" : "Your cart is empty"}
        </h1>

        <p className="mt-4 text-[14px] md:text-[15px] text-[#666]">
          {lang === "AR"
            ? "يرجى إضافة منتجات إلى السلة قبل إتمام الشراء."
            : "Please add products to your cart before checkout."}
        </p>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-8 w-full sm:w-[260px] h-[50px] bg-[#e99a22] text-white text-[15px] font-normal hover:opacity-90 active:scale-[0.99] transition"
        >
          {lang === "AR" ? "متابعة التسوق" : "Continue shopping"}
        </button>
      </div>
    </section>

    <Footer />
  </div>
);

if (cartLoading) {
  return <CheckoutSkeleton />;
}

if (!cartItems.length) {
  return <CheckoutEmptyState />;
}

  return (
  <div
    className="w-full min-h-screen bg-[#F6EFE9]"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    {loading && (
      <div className="fixed inset-0 bg-black/35 z-[999999] flex items-center justify-center px-5">
        <div className="bg-white px-8 py-7 shadow-[0_18px_55px_rgba(0,0,0,0.25)] text-center border border-[#e5e5e5]">
          <div className="loader mx-auto mb-4"></div>
          <p className="font-semibold text-black">
            {lang === "EN"
              ? "Processing your order..."
              : "جاري معالجة طلبك..."}
          </p>
        </div>
      </div>
    )}

    {/* PAGE TITLE */}
    <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 py-10 md:py-12 text-center">
        <button
          type="button"
          onClick={safeNavigateBack}
          className={`
            absolute
            mt-[-12px]
            text-[34px]
            leading-none
            text-black/60
            hover:text-black
            transition
            ${lang === "AR" ? "right-5 md:right-8" : "left-5 md:left-8"}
          `}
          aria-label="Back"
        >
          ×
        </button>

        <h1 className="text-[30px] md:text-[30px] leading-none font-bold text-[#333]">
          {t.title}
        </h1>

        <p className="mt-4 text-[14px] md:text-[15px] text-[#666]">
          {lang === "EN"
            ? "Complete your order safely and quickly."
            : "أكملي طلبك بسهولة وأمان."}
        </p>
      </div>
    </section>

    {/* CONTENT */}
    <section className="bg-[#F6EFE9] pb-14 md:pb-16">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 pt-6 md:pt-8">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_390px] gap-6 md:gap-8 items-start">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* CONTACT */}
            <section className={sectionCard}>
              <div className="flex items-center justify-between gap-4 mb-5">
                <h2 className="text-[20px] md:text-[20px] font-normal text-[#333]">
                  {t.contact}
                </h2>
                <span className="text-[12px] uppercase tracking-[0.12em] text-[#777]">
                  01
                </span>
              </div>

              <label className={labelStyle}>
                {lang === "EN" ? "Full Name" : "الاسم الكامل"}
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={lang === "EN" ? "Full Name" : "الاسم الكامل"}
                className={inputStyle}
              />
              {errors.fullName && (
                <p className={errorStyle}>
                  {lang === "EN" ? "Invalid name" : "الاسم غير صالح"}
                </p>
              )}

              <label className={labelStyle}>
                {lang === "EN" ? "Phone" : "رقم الهاتف"}
              </label>
              <input
                value={phone}
                onChange={handlePhoneChange}
                placeholder={lang === "EN" ? "Phone" : "رقم الهاتف"}
                className={inputStyle}
                dir="ltr"
              />
              {errors.phone && (
                <p className={errorStyle}>
                  {lang === "EN"
                    ? "Invalid Lebanese phone number"
                    : "رقم الهاتف اللبناني غير صالح"}
                </p>
              )}

              <label className={labelStyle}>
                {lang === "EN" ? "Email Optional" : "البريد الإلكتروني اختياري"}
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={
                  lang === "EN" ? "Email (optional)" : "البريد الإلكتروني اختياري"
                }
                className={inputStyle}
                dir="ltr"
              />
            </section>

            {/* SHIPPING METHOD */}
            <section className={sectionCard}>
              <div className="flex items-center justify-between gap-4 mb-5">
                <h2 className="text-[20px] md:text-[20px] font-normal text-[#333]">
                  {lang === "EN" ? "Shipping Method" : "طريقة الشحن"}
                </h2>
                <span className="text-[12px] uppercase tracking-[0.12em] text-[#777]">
                  02
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`
                    min-h-[64px]
                    border
                    px-4 py-4
                    flex items-center gap-3
                    cursor-pointer
                    transition
                    ${
                      shippingMethod === "delivery"
                        ? "border-black bg-[#fafafa]"
                        : "border-[#d8d8d8] bg-white"
                    }
                  `}
                >
                  <input
                    type="radio"
                    checked={shippingMethod === "delivery"}
                    onChange={() => setShippingMethod("delivery")}
                    className="accent-black"
                  />
                  <div>
                    <p className="text-[15px] font-semibold text-black">
                      {lang === "EN" ? "Delivery" : "توصيل"}
                    </p>
                    <p className="mt-1 text-[12px] text-[#777]">
                      {getDeliveryMessage()}
                    </p>
                  </div>
                </label>

                <label
                  className={`
                    min-h-[64px]
                    border
                    px-4 py-4
                    flex items-center gap-3
                    cursor-pointer
                    transition
                    ${
                      shippingMethod === "pickup"
                        ? "border-black bg-[#fafafa]"
                        : "border-[#d8d8d8] bg-white"
                    }
                  `}
                >
                  <input
                    type="radio"
                    checked={shippingMethod === "pickup"}
                    onChange={() => setShippingMethod("pickup")}
                    className="accent-black"
                  />
                  <div>
                    <p className="text-[15px] font-semibold text-black">
                      {lang === "EN" ? "Pickup from store" : "استلام من المتجر"}
                    </p>
                    <p className="mt-1 text-[12px] text-[#777]">
                      {lang === "EN" ? "No delivery fee" : "بدون رسوم توصيل"}
                    </p>
                  </div>
                </label>
              </div>
            </section>

            {/* DELIVERY DETAILS */}
            {shippingMethod === "delivery" && (
              <section className={sectionCard}>
                <div className="flex items-center justify-between gap-4 mb-5">
                  <h2 className="text-[20px] md:text-[20px] font-normal text-[#333]">
                    {lang === "EN" ? "Delivery Details" : "تفاصيل التوصيل"}
                  </h2>
                  <span className="text-[12px] uppercase tracking-[0.12em] text-[#777]">
                    03
                  </span>
                </div>

                <label className={labelStyle}>
                  {lang === "EN" ? "Governorate" : "المحافظة"}
                </label>
                <select
                  value={governorate}
                  onChange={(e) => setGovernorate(e.target.value)}
                  className={inputStyle}
                >
                  <option value="">
                    {lang === "EN" ? "Select Governorate" : "اختر المحافظة"}
                  </option>
                  {LEB_GOV.map((gov) => (
                    <option key={gov.en} value={gov.en}>
                      {lang === "EN" ? gov.en : gov.ar}
                    </option>
                  ))}
                </select>
                {errors.governorate && (
                  <p className={errorStyle}>
                    {lang === "EN" ? "Select governorate" : "اختاري المحافظة"}
                  </p>
                )}

                <label className={labelStyle}>
  {lang === "EN" ? "Area" : "المنطقة"}
</label>
<input
  value={area}
  onChange={(e) => setArea(e.target.value)}
  placeholder={lang === "EN" ? "Area" : "المنطقة"}
  className={inputStyle}
/>
{errors.area && (
  <p className={errorStyle}>
    {lang === "EN" ? "Enter area" : "أدخلي المنطقة"}
  </p>
)}

<label className={labelStyle}>
  {lang === "EN" ? "Street" : "الشارع"}
</label>
<input
  value={street}
  onChange={(e) => setStreet(e.target.value)}
  placeholder={lang === "EN" ? "Street" : "الشارع"}
  className={inputStyle}
/>
{errors.street && (
  <p className={errorStyle}>
    {lang === "EN" ? "Enter street" : "أدخلي الشارع"}
  </p>
)}

<label className={labelStyle}>
  {lang === "EN" ? "Building" : "المبنى"}
</label>
<input
  value={building}
  onChange={(e) => setBuilding(e.target.value)}
  placeholder={lang === "EN" ? "Building / Floor" : "المبنى / الطابق"}
  className={inputStyle}
/>
{errors.building && (
  <p className={errorStyle}>
    {lang === "EN" ? "Enter building" : "أدخلي المبنى"}
  </p>
)}
              </section>
            )}

            {/* NOTES */}
            <section className={sectionCard}>
              <div className="flex items-center justify-between gap-4 mb-5">
                <h2 className="text-[20px] md:text-[20px] font-normal text-[#333]">
                  {lang === "EN" ? "Notes Optional" : "ملاحظات اختياري"}
                </h2>
                <span className="text-[12px] uppercase tracking-[0.12em] text-[#777]">
                  {shippingMethod === "delivery" ? "04" : "03"}
                </span>
              </div>

              <textarea
                value={note}
                onFocus={() => setNoteFocused(true)}
                onBlur={() => setNoteFocused(false)}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  lang === "EN"
                    ? "Write any extra note for your order..."
                    : "اكتبي أي ملاحظة إضافية للطلب..."
                }
                className={textareaStyle}
              />
            </section>
          </div>

          {/* RIGHT COLUMN SUMMARY */}
          <aside className="md:sticky md:top-[160px] bg-[#F6EFE9] px-5 py-6 md:px-6 md:py-6 border border-[#e6e6e6]">
            <h2 className="text-[22px] md:text-[20px] font-normal text-[#333] mb-5">
              {t.summary}
            </h2>

            {/* ITEMS */}
            <div className="space-y-4 max-h-[280px] overflow-y-auto no-scrollbar pr-1">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-[54px_minmax(0,1fr)_auto] gap-3 items-start"
                >
                  <div className="w-[54px] h-[72px] bg-[#f5f5f5] overflow-hidden">
                    {String(item.image || "").match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                      <video
                        src={item.image}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover block"
                      />
                    ) : (
                      <img
                        src={item.image || "/images/placeholder-product.jpg"}
                        alt={lang === "EN" ? item.nameEN : item.nameAR}
                        className="w-full h-full object-cover block"
                      />
                    )}
                  </div>

                  <div className="min-w-0">
  <p className="text-[13px] leading-[1.35] text-black line-clamp-2">
    {lang === "EN" ? item.nameEN : item.nameAR}
  </p>

  <p className="mt-1 text-[12px] text-[#777]">
    {lang === "EN" ? "Qty" : "الكمية"}: {item.qty}
  </p>

  {(item.isBxgyFree || item.bxgyType === "free") && (
    <p className="mt-1 text-[12px] text-green-700 font-semibold">
      {lang === "AR" ? "هدية مجانية" : "Free gift"}
    </p>
  )}

  {(item.isBxgyDiscounted || item.bxgyType === "percentage") && (
    <p className="mt-1 text-[12px] text-green-700 font-semibold">
      {lang === "AR"
        ? `خصم ${Number(item.bxgyDiscountValue || 0)}%`
        : `${Number(item.bxgyDiscountValue || 0)}% BXGY discount`}
    </p>
  )}
</div>

<span className="text-[13px] font-semibold text-black">
  {item.isBxgyFree || item.bxgyType === "free"
    ? lang === "AR"
      ? "مجاني"
      : "FREE"
    : `$${(Number(item.price || 0) * Number(item.qty || 1)).toFixed(2)}`}
</span>
                </div>
              ))}
            </div>

            <div className="my-5 border-t border-black" />

            {/* COUPON */}
            {hasActiveCoupons && (
              <div className="mb-5">
                <label className={labelStyle}>
                  {lang === "EN" ? "Discount code" : "رمز الخصم"}
                </label>

                <div className="grid grid-cols-[1fr_96px] h-[42px]">
                  <input
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      setCouponError(null);
                    }}
                    placeholder={lang === "EN" ? "Coupon code" : "رمز الخصم"}
                    className="border border-[#cfcfcf] px-3 text-[14px] outline-none focus:border-black"
                  />

                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponLoading}
                    className="bg-black text-white text-[14px] disabled:opacity-50"
                  >
                    {couponLoading
                      ? "..."
                      : lang === "EN"
                      ? "Apply"
                      : "تطبيق"}
                  </button>
                </div>

                {couponError && (
                  <p className="text-[12px] mt-2 text-red-600 font-semibold">
                    {couponError === "unavailable"
                      ? lang === "EN"
                        ? "Unavailable code"
                        : "رمز غير صالح"
                      : lang === "EN"
                      ? "Coupon expired"
                      : "انتهت صلاحية الكوبون"}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <div className={lineRow}>
                <span>{lang === "EN" ? "Subtotal" : "المجموع"}</span>
                <span>${Number(subtotal || 0).toFixed(2)}</span>
              </div>

              {effectiveOrderDiscountAmount > 0 && (
  <div className="flex items-center justify-between gap-4 text-[14px] text-green-700">
    <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
    <span>- ${effectiveOrderDiscountAmount.toFixed(2)}</span>
  </div>
)}

              {coupon && (
                <div className="flex items-center justify-between gap-4 text-[14px] text-green-700">
                  <span>{lang === "AR" ? "كوبون" : "Coupon"}</span>
                  <span>- ${Number(coupon.discount || 0).toFixed(2)}</span>
                </div>
              )}

              <div className={lineRow}>
  <span>
    {shippingMethod === "pickup"
      ? lang === "EN"
        ? "Pickup"
        : "استلام"
      : lang === "EN"
      ? "Delivery"
      : "توصيل"}
  </span>

  <span>
    {deliveryFee === 0
      ? isFreeDeliveryFromDiscount
        ? lang === "AR"
          ? "مجاني (خصم)"
          : "Free (discount)"
        : lang === "EN"
        ? "Free"
        : "مجاني"
      : `$${Number(deliveryFee || 0).toFixed(2)}`}
  </span>
</div>

{shippingMethod === "delivery" && freeShippingDiscount && (
  <div
    className={`
      border px-3 py-3 text-[12px] leading-[1.45]
      ${
        isFreeDeliveryFromDiscount
          ? "border-green-200 bg-green-50 text-green-800"
          : "border-orange-200 bg-orange-50 text-orange-800"
      }
    `}
  >
    <div className="font-semibold">
      {freeShippingInfo.label}
    </div>

    {freeShippingInfo.endLabel && (
      <div className="mt-1 opacity-80">
        {lang === "AR"
          ? `ينتهي في ${freeShippingInfo.endLabel}`
          : `Ends on ${freeShippingInfo.endLabel}`}
      </div>
    )}
  </div>
)}
            </div>

            <div className="my-5 border-t border-black" />

            <div className="flex items-center justify-between gap-4 text-[20px] font-bold text-black">
              <span>{lang === "EN" ? "Total" : "الإجمالي"}</span>
              <span>${checkoutTotal.toFixed(2)}</span>
            </div>

            <button
              type="button"
              onClick={submitOrder}
              disabled={loading}
              className={`
                mt-6
                w-full h-[52px]
                text-white
                text-[16px]
                font-normal
                transition
                ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#e99a22] hover:opacity-90 active:scale-[0.99]"}
              `}
            >
              {loading
                ? lang === "EN"
                  ? "Placing Order..."
                  : "جاري إرسال الطلب..."
                : t.placeOrder}
            </button>

            <div className="mt-5 bg-[#F6EFE9] px-4 py-3 text-[13px] leading-[1.5] text-[#555]">
              {lang === "EN"
                ? "Don't wait with your purchase, adding products to your bag does not mean that they are reserved."
                : "لا تؤجلي عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."}
            </div>

            <p className="text-center mt-4 text-[13px] text-[#666]">
              {getDeliveryMessage()}
            </p>
          </aside>
        </div>
      </div>
    </section>

    <Footer />

    <style>{`
      .loader {
        width: 20px;
        height: 20px;
        border: 2px solid rgba(0,0,0,0.15);
        border-top: 2px solid #111;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);
}

export default CheckoutPage;